// framework/client/components/CalendarView.js
import { ComponentBase } from "../core/ComponentBase.js";
import CalendarEventSchema from "../../schemas/collections/calendarEvents.js";

const FULLCALENDAR_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/fullcalendar@6.1.15/index.global.min.js';
let fullCalendarPromise = null;

function loadFullCalendarScript() {
    if (window.FullCalendar) return Promise.resolve();
    if (fullCalendarPromise) return fullCalendarPromise;

    fullCalendarPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = FULLCALENDAR_SCRIPT_URL;
        script.onload = () => {
            console.log("FullCalendar script loaded successfully.");
            resolve();
        };
        script.onerror = () => {
            console.error("Failed to load FullCalendar script.");
            reject(new Error(`Failed to load script: ${FULLCALENDAR_SCRIPT_URL}`));
        };
        document.head.appendChild(script);
    });
    return fullCalendarPromise;
}

export class CalendarView extends ComponentBase {
    static styles = `
        .calendar-view-container { height: 100%; }
        
        .fc { 
            height: 100%;
            --fc-border-color: var(--bs-border-color);
            --fc-today-bg-color: var(--bs-primary-bg-subtle);
            --fc-button-text-color: var(--bs-primary-contrast, #fff);
            --fc-button-bg-color: var(--bs-primary);
            --fc-button-border-color: var(--bs-primary);
            --fc-button-hover-bg-color: color-mix(in srgb, var(--bs-primary) 85%, black);
            --fc-button-hover-border-color: color-mix(in srgb, var(--bs-primary) 80%, black);
            --fc-button-active-bg-color: color-mix(in srgb, var(--bs-primary) 80%, black);
            --fc-button-active-border-color: color-mix(in srgb, var(--bs-primary) 75%, black);
        }
        
        .fc .fc-header-toolbar .fc-button {
            background-color: var(--fc-button-bg-color) !important;
            border-color: var(--fc-button-border-color) !important;
            color: var(--fc-button-text-color) !important;
            text-transform: capitalize;
            opacity: 0.9;
            transition: opacity 0.2s ease-in-out;
        }
        .fc .fc-header-toolbar .fc-button:hover {
            background-color: var(--fc-button-hover-bg-color) !important;
            border-color: var(--fc-button-hover-border-color) !important;
            opacity: 1;
        }
        .fc .fc-header-toolbar .fc-button-primary:not(:disabled).fc-button-active, 
        .fc .fc-header-toolbar .fc-button-primary:not(:disabled):active {
            background-color: var(--fc-button-active-bg-color) !important;
            border-color: var(--fc-button-active-border-color) !important;
            color: var(--fc-button-text-color) !important;
            opacity: 1;
            box-shadow: var(--ab-shadow-none);
        }
        .fc-list-event-time { color: var(--bs-secondary-color); }

        /* --- NEW: Styles for the GenericFormRenderer Modal --- */
        .calendar-event-modal .modal-dialog {
            max-width: 500px;
        }
        .calendar-event-modal .modal-footer {
            display: flex;
            justify-content: space-between;
            width: 100%;
        }
        .calendar-event-modal .delete-btn-container {
            margin-right: auto; /* Pushes delete button to the far left */
        }

        /* --- NEW: Mobile Responsiveness for Calendar Header --- */
        @media (max-width: 768px) {
            .fc .fc-header-toolbar {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
            .fc .fc-header-toolbar .fc-toolbar-chunk {
                display: flex;
                justify-content: center;
                width: 100%;
            }
        }
    `;


    constructor(config) {
        super(config);
        this.isEditable = this.config.isEditable !== false;
        this.calendar = null;
        this.formRenderer = null;
        this.currentEditingEvent = null;

        this.eventColorMap = this.config.eventColorMap || {
            'class': '#3788d8', 'assessment': '#f5a623', 'personal': '#2ecc71',
            'missed': '#e74c3c', 'cancelled': '#95a5a6'
        };
        console.log(this.config);
        this.applyStyles();
    }


    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'calendar-view-container';

        const formConfig = {
            labelMode: 'floating',
            showActions: false,
            fields: [
                { name: "title", type: "text", label: "Title", required: true },
                { name: "start", type: "datetime-local", label: "Start Time", required: true },
                { name: "end", type: "datetime-local", label: "End Time" },
                { name: "eventType", type: "text", label: "Event Type", placeholder: "e.g., class, personal" },
                { name: "description", type: "textarea", label: "Description", rows: 3 },
                { name: "allDay", type: "checkbox", label: "All-day event" }
            ],
        };

        this.formRenderer = await this.createManagedComponent('GenericFormRenderer', null, {
            instanceId: `${this.config.instanceId}-event-form`,
            formId: 'calendarEventForm',
            formConfig: formConfig,
            schema: CalendarEventSchema.omit({ contextId: true })
        });

        await super.initialize();
    }


    async initAfterLogin() {
        if (this.contextId) {
            await this.renderCalendar();
        } else {
            this.render();
        }
    }

    async refresh() {
        if (this.contextId && !this.calendar) {
            await this.renderCalendar();
        } else if (this.calendar) {
            this.calendar.updateSize();
        }
    }



    render() {
        if (!this.contextId) {
            this.container.innerHTML = `<p class="p-3 text-muted text-center">${this.t('No context selected for calendar.')}</p>`;
        }
        return this.container;
    }

    async renderCalendar() {
        if (this.calendar) {
            this.calendar.refetchEvents();
            this.calendar.updateSize();
            return;
        }

        const calendarEl = document.createElement('div');
        this.container.innerHTML = '';
        this.container.appendChild(calendarEl);

        try {
            await loadFullCalendarScript();
        } catch (error) {
            this.log.error(error.message);
            this.container.innerHTML = `<div class="alert alert-danger">${this.t('Error: Could not load the calendar library.')}</div>`;
            return;
        }

        this.calendar = new FullCalendar.Calendar(calendarEl, {
            height: '100%',
            initialView: 'dayGridMonth',
            headerToolbar: {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
            },
            // --- NEW: Add icons to buttons ---
            buttonText: {
                today: this.t('Today'),
                month: this.t('Month'),
                week: this.t('Week'),
                day: this.t('Day'),
                list: this.t('List')
            },
            buttonIcons: {
                prev: 'chevron-left',
                next: 'chevron-right',
                prevYear: 'chevrons-left',
                nextYear: 'chevrons-right'
            },
            // --- END NEW ---
            editable: this.isEditable,
            selectable: this.isEditable,
            events: this.fetchEvents.bind(this),
            select: this.isEditable ? this.handleDateSelect.bind(this) : null,
            eventClick: this.handleEventClick.bind(this),
            eventDrop: this.isEditable ? this.handleEventDrop.bind(this) : null,
            eventResize: this.isEditable ? this.handleEventResize.bind(this) : null,
            eventDidMount: (arg) => {
                const eventType = arg.event.extendedProps.eventType;
                const status = arg.event.extendedProps.status;
                const color = this.eventColorMap[status] || this.eventColorMap[eventType];
                if (color) {
                    arg.el.style.backgroundColor = color;
                    arg.el.style.borderColor = color;
                }
            },
            datesSet: () => {
                if (this.calendar && this.calendar.el) {
                    this.appClient.tx.applyTranslations(this.calendar.el);
                }
            },
        });

        this.calendar.render();
        this.appClient.tx.applyTranslations(this.calendar.el);
    }

    fetchEvents(fetchInfo, successCallback, failureCallback) {
        this.appClient.rpcClient.call('CalendarRpc.getEvents', {
            contextId: this.contextId,
            start: fetchInfo.startStr,
            end: fetchInfo.endStr
        }).then(events => {
            successCallback(events.map(e => ({ ...e, id: e._id, start: e.start, end: e.end })));
        }).catch(error => {
            this.appClient.notifier.error("Failed to load calendar events.");
            failureCallback(error);
        });
    }


    async showEventModal(eventData = {}) {
        this.currentEditingEvent = eventData;
        const isNewEvent = !eventData._id;

        const modalId = `${this.config.instanceId}-event-modal`;
        document.getElementById(modalId)?.remove(); // Clean up previous modal

        const modalElement = document.createElement('div');
        modalElement.className = 'modal fade calendar-event-modal';
        modalElement.id = modalId;
        modalElement.setAttribute('tabindex', '-1');

        const modalDialog = document.createElement('div');
        modalDialog.className = 'modal-dialog modal-dialog-centered';
        modalElement.appendChild(modalDialog);

        const modalContent = document.createElement('div');
        modalContent.className = 'modal-content';
        modalDialog.appendChild(modalContent);

        const modalHeader = document.createElement('div');
        modalHeader.className = 'modal-header';
        modalHeader.innerHTML = `<h5 class="modal-title">${this.t(isNewEvent ? 'Add Event' : 'Edit Event')}</h5><button type="button" class="btn-close" data-bs-dismiss="modal"></button>`;
        modalContent.appendChild(modalHeader);

        const modalBody = document.createElement('div');
        modalBody.className = 'modal-body';
        modalContent.appendChild(modalBody);

        const formElement = await this.formRenderer.render({});
        modalBody.appendChild(formElement);

        const modalFooter = document.createElement('div');
        modalFooter.className = 'modal-footer';
        modalContent.appendChild(modalFooter);

        // Add Delete button to the left
        const deleteBtnContainer = document.createElement('div');
        deleteBtnContainer.className = 'delete-btn-container';
        if (!isNewEvent && this.isEditable) {
            const deleteBtn = document.createElement('button');
            deleteBtn.type = 'button';
            deleteBtn.className = 'btn btn-danger';
            deleteBtn.textContent = this.t('Delete');
            deleteBtn.addEventListener('click', () => this._handleDeleteEvent());
            deleteBtnContainer.appendChild(deleteBtn);
        }
        modalFooter.appendChild(deleteBtnContainer);

        // Add Cancel and Save buttons to the right
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'btn btn-secondary';
        cancelBtn.textContent = this.t('Cancel');
        cancelBtn.setAttribute('data-bs-dismiss', 'modal');
        modalFooter.appendChild(cancelBtn);

        const saveBtn = document.createElement('button');
        saveBtn.type = 'button';
        saveBtn.className = 'btn btn-primary';
        saveBtn.textContent = this.t('Save');
        saveBtn.addEventListener('click', () => this._handleSaveEvent());
        modalFooter.appendChild(saveBtn);

        document.body.appendChild(modalElement);
        const bsModal = new bootstrap.Modal(modalElement);

        // Populate form with data
        const formData = { ...eventData };
        const toLocalISOString = (date) => {
            if (!date) return '';
            const d = new Date(date);
            const tzoffset = d.getTimezoneOffset() * 60000;
            return new Date(d.getTime() - tzoffset).toISOString().slice(0, 16);
        };
        formData.start = toLocalISOString(formData.start || Date.now());
        formData.end = toLocalISOString(formData.end);
        this.formRenderer.setData(formData);

        modalElement.addEventListener('hidden.bs.modal', () => modalElement.remove(), { once: true });
        bsModal.show();
    }

    async _handleSaveEvent() {
        if (!await this.formRenderer.isFormValid()) {
            this.log.warn("Event form validation failed.");
            return;
        }

        const formData = this.formRenderer.getFormData();
        const dataToSave = {
            ...this.currentEditingEvent,
            ...formData,
            contextId: this.contextId,
            start: new Date(formData.start).toISOString(),
            end: formData.end ? new Date(formData.end).toISOString() : null,
        };

        const isUpdate = !!dataToSave._id;
        const rpcMethod = isUpdate ? 'CalendarRpc.update' : 'CalendarRpc.create';

        try {
            await this.appClient.rpcClient.call(rpcMethod, dataToSave);
            this.appClient.notifier.success(`Event ${isUpdate ? 'updated' : 'created'}!`);
            this.calendar.refetchEvents();
            const modal = document.getElementById(`${this.config.instanceId}-event-modal`);
            bootstrap.Modal.getInstance(modal)?.hide();
        } catch (error) {
            this.appClient.notifier.error(`Failed to save event: ${error.message}`);
        }
    }

    async _handleDeleteEvent() {
        const eventId = this.currentEditingEvent?._id;
        if (!eventId) return;

        const confirmed = await this.appClient.notifier.confirm("Are you sure you want to delete this event?", { type: 'danger' });
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('CalendarRpc.delete', { _id: eventId });
                this.appClient.notifier.success("Event deleted.");
                this.calendar.refetchEvents();
                const modal = document.getElementById(`${this.config.instanceId}-event-modal`);
                bootstrap.Modal.getInstance(modal)?.hide();
            } catch (error) {
                this.appClient.notifier.error(`Failed to delete event: ${error.message}`);
            }
        }
    }

    handleDateSelect(selectionInfo) { this.showEventModal({ start: selectionInfo.start, end: selectionInfo.end, allDay: selectionInfo.allDay }); }
    handleEventClick(clickInfo) { this.showEventModal({ ...clickInfo.event.extendedProps, _id: clickInfo.event.id, start: clickInfo.event.start, end: clickInfo.event.end, allDay: clickInfo.event.allDay }); }
    async handleEventDrop(dropInfo) { await this._handleSaveEvent({ _id: dropInfo.event.id, start: dropInfo.event.start, end: dropInfo.event.end || dropInfo.event.start, allDay: dropInfo.event.allDay }); }
    async handleEventResize(resizeInfo) { await this._handleSaveEvent({ _id: resizeInfo.event.id, start: resizeInfo.event.start, end: resizeInfo.event.end }); }

    destroy() {
        if (this.calendar) {
            this.calendar.destroy();
            this.calendar = null;
        }
        // The form renderer is a managed component, so its destroy is handled by ComponentBase.
        // We just need to ensure any manually created modal DOM is removed.
        const modalElement = document.getElementById(`${this.config.instanceId}-event-modal`);
        if (modalElement) {
            bootstrap.Modal.getInstance(modalElement)?.dispose();
            modalElement.remove();
        }
        super.destroy();
    }

}